import moment from "moment";
import {StringBuilder} from "../../../AppUtils.js";
import {calculateDeliveryTime} from "../../../event/utils.jsx";
import React from "react";
import {getOrderPrice} from "../../../utils/calculationUtils.jsx";

export const DeliveryProviderDeliveryStatus = {
    pending: "pending",
    arrivedAtBusiness: "arrivedAtBusiness",
    pickedUp: "pickedUp",
    onTheWay: "onTheWay",
    arrivedAtDestination: "arrivedAtDestination",
    supplied: "supplied",
    failed: "failed"
};

export const DeliveryStatusToColor = {
    [DeliveryProviderDeliveryStatus.pending]: ["", "#e7ebed"],
    [DeliveryProviderDeliveryStatus.supplied]: ["var(--lightes-accept-color)", "var(--light-accept-color)"],
    [DeliveryProviderDeliveryStatus.failed]: ["var(--error-color)", "var(--transparent-error-color)"],
    [DeliveryProviderDeliveryStatus.onTheWay]: ["", "#e7ebed"],
    [DeliveryProviderDeliveryStatus.pickedUp]: ["", "#e7ebed"],
    [DeliveryProviderDeliveryStatus.arrivedAtBusiness]: ["", "#e7ebed"],
    [DeliveryProviderDeliveryStatus.arrivedAtDestination]: ["", "#e7ebed"]
};

export const deliveryOptions = ({onShowDeliveryClick, onShowHistoryOrder}) => [
    {
        title: "צפייה בזמנה",
        onClick: onShowDeliveryClick
    },
    {
        title: "היסטוריית משלוח",
        onClick: onShowHistoryOrder
    }
];

export const DeliveryProviderDeliveryStatusToText = {
    [DeliveryProviderDeliveryStatus.pending]: "ממתין",
    [DeliveryProviderDeliveryStatus.supplied]: "נמסר",
    [DeliveryProviderDeliveryStatus.failed]: "נכשל",
    [DeliveryProviderDeliveryStatus.onTheWay]: "יוצא לכיוון",
    [DeliveryProviderDeliveryStatus.pickedUp]: "נאסף",
    [DeliveryProviderDeliveryStatus.arrivedAtBusiness]: "הגיע לבית העסק",
    [DeliveryProviderDeliveryStatus.arrivedAtDestination]: "הגיע ליעד"
};

export const DeliveryShortcutsKeys = {
    todayDeliveries: "todayDeliveries",
    tomorrowDeliveries: "tomorrowDeliveries",
    currentWeek: "currentWeek"
};

export const DeliveriesShortcuts = [
    {
        key: DeliveryShortcutsKeys.todayDeliveries,
        label: "היום",
        value: DeliveryShortcutsKeys.todayDeliveries,
        datePicked: () => [moment().startOf("day"), moment().endOf("day")],
        isChecked: ({searchDate}) =>
            searchDate.length > 1 &&
            moment()?.diff(searchDate?.[0], "days") === 0 &&
            moment()?.diff(searchDate?.[1], "days") === 0
    },
    {
        key: DeliveryShortcutsKeys.tomorrowDeliveries,
        label: "מחר",
        value: DeliveryShortcutsKeys.tomorrowDeliveries,
        datePicked: () => [moment().add(1, "day").startOf("day"), moment().add(1, "day").endOf("day")],
        isChecked: ({searchDate}) =>
            searchDate?.length > 1 &&
            searchDate?.[0]?.endOf("day")?.diff(moment(), "days") === 1 &&
            searchDate?.[0]?.endOf("day")?.diff(moment(), "days") === 1
    },
    {
        key: DeliveryShortcutsKeys.currentWeek,
        label: "השבוע",
        value: DeliveryShortcutsKeys.currentWeek,
        datePicked: () => [moment().startOf("week"), moment().endOf("week")],
        isChecked: ({searchDate}) =>
            searchDate?.length > 1 &&
            moment(searchDate?.[0]).isSame(moment().startOf("week"), "day") &&
            moment(searchDate?.[1]).isSame(moment().endOf("week"), "day")
    }
];

export const DeliveryProviderDeliveriesProjection = [
    "deliveryId",
    "status",
    "sourceAddress",
    "destinationAddress",
    "price",
    "until",
    "dtstart",
    "driverId",
    "companyName",
    "proposalId",
    "eventId",
    "orderId",
    "statusMessage"
];

export const deliveryProviderDriversProjection = ["driverId", "name", "isExternal"];

export const deliveriesPageSize = 50;

export const deliveryItemHeaderConfig = (
    {driverIdToDriver, hideDriverTag} = {
        driverIdToDriver: {},
        hideDriverTag: false
    }
) => {
    return {
        tags: [
            {
                className: "tag-proposal-id",
                key: "proposalId"
            },
            {
                key: "driverId",
                className: ({driverId}) => (!driverId ? "driver-tag" : "tag-proposal-id"),
                render: ({driverId}) => driverIdToDriver?.[driverId]?.name,
                shouldHide: ({driverId}) => hideDriverTag || !driverId
            },
            {
                key: "noDriverTag",
                className: "no-driver-tag",
                render: () => "ללא שליח",
                shouldHide: ({driverId}) => hideDriverTag || !!driverId
            },
            {
                key: "status",
                render: ({status}) => DeliveryProviderDeliveryStatusToText[status],
                color: ({status}) => DeliveryStatusToColor?.[status]?.[0],
                backgroundColor: ({status}) => DeliveryStatusToColor?.[status]?.[1],
                shouldHide: ({status}) => status === DeliveryProviderDeliveryStatus.pending
            },
            {
                key: "price",
                className: ({order}) =>
                    order?.offeredPrice < 4000 ? "tag-proposal-id tag-price-left" : "driver-tag tag-price-left",
                render: ({order}) => {
                    const {deliveryPrice, ...orderWithoutDelivery} = order;
                    return new StringBuilder(getOrderPrice(orderWithoutDelivery).toFixed(2)).append("₪").toString();
                }
            }
        ],
        description: [
            {
                className: "tag-index",
                render: ({index}) => index + 1
            },
            {
                className: "delivery-provider-address-title",
                render: ({order, sourceAddress, companyName}) => (
                    <div className="source-wrapper">
                        <div style={{whiteSpace: "nowrap", width: "fit-content"}}>
                            {new StringBuilder("איסוף")
                                .append(calculateDeliveryTime(order)?.format("HH:mm"))
                                .append("-")
                                .toString()}
                        </div>
                        <div style={{fontWeight: "400"}}>
                            {new StringBuilder(order?.providerBrand)
                                .append(" | ")
                                .append(sourceAddress?.address)
                                .toString()}
                        </div>
                    </div>
                )
            },
            {
                className: "delivery-provider-address-title",
                render: ({dtstart, until, destinationAddress}) => (
                    <div className="source-wrapper">
                        <div style={{whiteSpace: "nowrap", width: "fit-content"}}>
                            {new StringBuilder("מסירה")
                                .append(moment(dtstart).format("HH:mm"))
                                .append("-")
                                .append(moment(until ?? dtstart).format("HH:mm"))
                                .append("-")
                                .toString()}
                        </div>
                        <div style={{fontWeight: "400"}}>
                            {new StringBuilder(destinationAddress?.address).toString()}
                        </div>
                    </div>
                )
            }
        ]
    };
};
